import React, { useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { Modal, Tab, Tabs } from 'react-bootstrap'

type Props = {
  show: boolean
  onHide: () => void
  title?: string
  categories: string[]
  series: number[]
  source?: any
  rawProfileData?: any
}

const ProfileChart: React.FC<Props> = ({ show, onHide, title, categories, series, source }) => {
  const [activeKey, setActiveKey] = useState<string>('data')
  const options: any = {
    chart: {
      type: 'line',
      toolbar: { show: true },
      id: 'profile-chart-view',
    },
    stroke: { width: 2, curve: 'smooth' },
    xaxis: {
      categories,
      title: { text: 'Time (HH:mm)' },
      labels: { rotate: -45 },
    },
    yaxis: {
      decimalsInFloat: 2,
      title: { text: 'Value' },
    },
    dataLabels: { enabled: false },
    tooltip: { x: { show: true } },
  }
  const seriesData = [
    {
      name: 'Profile',
      data: series,
    },
  ]
  const highlightedSource = useMemo(() => {
    try {
      const jsonStr =
        typeof source === 'string'
          ? JSON.stringify(JSON.parse(source), null, 2)
          : JSON.stringify(source ?? {}, null, 2)
      return syntaxHighlight(jsonStr)
    } catch (e) {
      const fallback = typeof source === 'string' ? source : JSON.stringify(source ?? {}, null, 2)
      return syntaxHighlight(fallback)
    }
  }, [source])

  return (
    <Modal
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered modal-xl'
      size='xl'
      show={show}
      backdrop={true}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Profile Chart'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          id='profile-chart-tabs'
          activeKey={activeKey}
          onSelect={(k) => setActiveKey(k || 'data')}
          className='mb-3'
        >
          <Tab eventKey='data' title='Profile Data'>
            <div className='w-100'>
              <ReactApexChart options={options} series={seriesData} type='line' height={420} />
            </div>
          </Tab>
          <Tab eventKey='source' title='Source Log'>
            <div
              className='rounded border'
              style={{ maxHeight: 440, overflow: 'auto', background: '#0b0f19' }}
            >
              <pre
                className='m-0 p-3'
                style={{ color: '#e0e0e0' }}
                dangerouslySetInnerHTML={{ __html: highlightedSource }}
              ></pre>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  )
}

function syntaxHighlight(json: string): string {
  const escJson = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escJson.replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    function (match) {
      let cls = 'text-warning'
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'text-primary' // key
        } else {
          cls = 'text-success' // string
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-info' // boolean
      } else if (/null/.test(match)) {
        cls = 'text-muted' // null
      } else {
        cls = 'text-danger' // number
      }
      return '<span class="' + cls + '">' + match + '</span>'
    }
  )
}

export default ProfileChart
